import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { Button, Form, Input, DatePicker, Select, Radio, Checkbox } from "antd";
import Loader from "../../../../components/Loader.jsx";
import { GetAllCountryApi } from "../../../../api/request/common.js";
import {
  addUpdateCompanyInfo,
  getCompanyInfo,
  getNatureOfBusiness,
  getTaxDomicilesReasons,
} from "../../../../api/request/corporate.js";
import { handleApiError } from "../../../../utils/handleApiError.js";
import { mobileNumberValidator } from "../../../../utils/utils.js";

const CRS_PASSIVE_NFE_WITH_CONTROLLING = 1;
const CRS_CLASSIFICATION_OPTIONS = [
  { value: 1, label: "Passive NFE with Controlling Reportable Person" },
  { value: 2, label: "CRS Reportable Person" },
  { value: 3, label: "Passive NFE that is CRS Reportable Person" },
  { value: 4, label: "Financial Institution" },
  { value: 5, label: "Active NFE" },
];
const EMPTY_CONTROLLING_PERSON = {
  fullName: "",
  taxIdentificationNumber: "",
  countryOfTaxResidenceId: null,
  tinNotApplicableReason: "",
};

const CompanyInfo = ({
  onNext,
  onPrevious,
  id,
  userId,
  isSubmitted,
  isReviewMode = false,
}) => {
  const [form] = Form.useForm();
  const [countries, setCountries] = useState([]);
  const [countryCodes, setCountryCodes] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [natures, setNatures] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sameAsAbove, setSameAsAbove] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const isMultiTaxDomicile = Form.useWatch("isMultiTaxDomicileEnabled", form);
  const crsClassifications = Form.useWatch("crsClassifications", form) || [];

  const isDisabled = isSubmitted;
  const isFieldDisabled = isDisabled && !isReviewMode;

  const getAllCountries = async () => {
    try {
      const { data } = await GetAllCountryApi();
      if (data?.success) {
        setCountries(
          data?.data?.map((item, i) => ({
            key: i,
            value: item?.id,
            label: item?.countryName,
          }))
        );

        setCountryCodes(
          data?.data
            ?.map((item, i) => {
              const dialCode =
                item?.countryCode1 || item?.dialCode || item?.countryCode;
              return {
                key: i,
                value: dialCode,
                label: dialCode
                  ? `${dialCode}${item?.countryCode2 ? ` (${item.countryCode2})` : ""}`
                  : "",
                countryCode: dialCode,
              };
            })
            ?.filter((item) => item?.value)
        );
      }
    } catch (error) {}
  };

  const getAllReasons = async () => {
    try {
      const { data } = await getTaxDomicilesReasons();
      if (data?.success) {
        setReasons(
          data?.data?.map((item) => ({ value: item?.id, label: item?.name }))
        );
      }
    } catch (error) {}
  };

  const getAllNatures = async () => {
    try {
      const { data } = await getNatureOfBusiness();
      if (data?.success) {
        setNatures(
          data?.data?.map((item) => ({ value: item?.id, label: item?.name }))
        );
      }
    } catch (error) {}
  };

  const getCompanyInfoData = async () => {
    try {
      const { data } = await getCompanyInfo(id);
      if (data?.success) {
        const phone = data?.data?.companyPhoneNumber ?? "";
        const [countryCode, phoneNumber] = phone.trim().split(" ");
        const savedCrsClassifications = data?.data?.crsClassifications || [];
        const needsControllingPersons = savedCrsClassifications.includes(
          CRS_PASSIVE_NFE_WITH_CONTROLLING
        );

        form.setFieldsValue({
          ...data?.data,
          dateOfIncorporation: dayjs(data?.data?.dateOfIncorporation) || null,
          countryCode: countryCode || null,
          phoneNumber: phoneNumber || null,
          crsClassifications: savedCrsClassifications,
          crsControllingPersons:
            data?.data?.crsControllingPersons?.length > 0
              ? data.data.crsControllingPersons
              : needsControllingPersons
                ? [{ ...EMPTY_CONTROLLING_PERSON }]
                : [],
        });
      }
    } catch (error) {}
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const promises = [getAllCountries(), getAllReasons(), getAllNatures()];

        if (id) promises.push(getCompanyInfoData());

        await Promise.all(promises);
      } catch (error) {
        handleApiError(error);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [id]);

  const filterOption = (input, option) => {
    return (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
  };

  const handleMultiTaxResidentChange = (e) => {
    const isMultiTaxDomicile = e?.target?.value;
    const taxDomiciles = form.getFieldValue("taxDomiciles") || [];

    if (isMultiTaxDomicile) {
      if (!taxDomiciles?.length) {
        form.setFieldsValue({
          taxDomiciles: [
            {
              taxDomicileCountryId: null,
              taxIDNumber: "",
              isTINIDAvailable: false,
              taxDomicileReasonID: null,
              alternateTaxIdentifier: "",
            },
          ],
        });
      }
    } else {
      if (taxDomiciles?.length) {
        form.setFieldsValue({
          taxDomiciles: [taxDomiciles[0]],
        });
      }
    }
  };

  const handleSameAsAboveChange = (e) => {
    const checked = e.target.checked;
    setSameAsAbove(checked);

    if (checked) {
      const registeredAddress = form.getFieldsValue([
        "companyApartmentVillaInfo",
        "companyStreetAreaInfo",
        "companyCountryCode",
        "companyCity",
        "companyZipCode",
      ]);

      form.setFieldsValue({
        businessApartmentVillaInfo: registeredAddress.companyApartmentVillaInfo,
        businessStreetAreaInfo: registeredAddress.companyStreetAreaInfo,
        businessCountryCode: registeredAddress.companyCountryCode,
        businessCity: registeredAddress.companyCity,
        businessZipCode: registeredAddress.companyZipCode,
      });
    } else {
      form.setFieldsValue({
        businessApartmentVillaInfo: "",
        businessStreetAreaInfo: "",
        businessCountryCode: undefined,
        businessCity: "",
        businessZipCode: "",
      });
    }
  };

  const handleRegisteredAddressChange = () => {
    if (sameAsAbove) {
      const registeredAddress = form.getFieldsValue([
        "companyApartmentVillaInfo",
        "companyStreetAreaInfo",
        "companyCountryCode",
        "companyCity",
        "companyZipCode",
      ]);

      form.setFieldsValue({
        businessApartmentVillaInfo: registeredAddress.companyApartmentVillaInfo,
        businessStreetAreaInfo: registeredAddress.companyStreetAreaInfo,
        businessCountryCode: registeredAddress.companyCountryCode,
        businessCity: registeredAddress.companyCity,
        businessZipCode: registeredAddress.companyZipCode,
      });
    }
  };

  const handleSubmit = async (values) => {
    if (isDisabled) return;
    setIsSubmitLoading(true);
    try {
      const payload = {
        ...values,
        companyPhoneNumber: `${values?.countryCode} ${values?.phoneNumber}`,
        taxDomiciles:
          values?.taxDomiciles?.map(
            ({
              taxDomicileReasonID,
              texIDNumber,
              isTINIDAvailable,
              ...item
            }) => {
              const value = { ...item, isTINIDAvailable };
              if (isTINIDAvailable) {
                value.taxIDNumber = "";
                value.taxDomicileReasonID = taxDomicileReasonID;
                value.alternateTaxIdentifier = item.alternateTaxIdentifier || "";
              } else {
                value.texIDNumber = texIDNumber;
                value.taxDomicileReasonID = null;
                value.alternateTaxIdentifier = "";
              }
              return value;
            }
          ) || [],
        crsClassifications: values?.crsClassifications || [],
        crsControllingPersons: (values?.crsClassifications || []).includes(
          CRS_PASSIVE_NFE_WITH_CONTROLLING
        )
          ? values?.crsControllingPersons || []
          : [],
        userId,
      };

      if (id) Object.assign(payload, { id });

      const { data } = await addUpdateCompanyInfo(payload);

      if (data?.success) {
        toast.success(data?.message);
        onNext();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <Form
        form={form}
        size="large"
        layout="vertical"
        autoComplete="off"
        className={isReviewMode ? "corporate-review-readonly" : undefined}
        disabled={isFieldDisabled}
        onKeyDown={(event) => {
          if (!isReviewMode) return;
          if (event.target.closest?.(".corporate-review-nav")) return;
          event.preventDefault();
        }}
        onFinish={handleSubmit}
        // validateTrigger={['onChange', 'onBlur', 'onSubmit']}
        initialValues={{
          isMultiTaxDomicileEnabled: false,
          crsClassifications: [],
          crsControllingPersons: [{ ...EMPTY_CONTROLLING_PERSON }],
          taxDomiciles: [
            {
              taxDomicileCountryId: null,
              taxIDNumber: "",
              isTINIDAvailable: false,
              taxDomicileReasonID: null,
              alternateTaxIdentifier: "",
            },
          ],
        }}
      >
        <div className="border border-gray-300 rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Company Name */}
            <Form.Item
              label="Company Name"
              name="companyName"
              rules={[{ required: true, message: "Please enter company name" }]}
            >
              <Input placeholder="Enter company name" />
            </Form.Item>

            {/* Date Of Incorporation */}
            <Form.Item
              label="Date Of Incorporation"
              name="dateOfIncorporation"
              rules={[
                {
                  required: true,
                  message: "Please select date of incorporation",
                },
              ]}
            >
              <DatePicker
                className="w-full"
                placeholder="Select date"
                format="DD-MM-YYYY"
              />
            </Form.Item>

            {/* Country of Incorporation */}
            <Form.Item
              label="Country of Incorporation"
              name="placeOfIncCorporation"
              rules={[
                {
                  required: true,
                  message: "Please select country of incorporation",
                },
              ]}
            >
              <Select
                showSearch
                placeholder="Select country"
                filterOption={filterOption}
                options={countries}
              />
            </Form.Item>

            {/* Company Registration Number */}
            <Form.Item
              label="Company Registration Number"
              name="companyRegistrationNumber"
              rules={[
                {
                  required: true,
                  message: "Please enter company registration number",
                },
              ]}
            >
              <Input placeholder="Enter company registration number" />
            </Form.Item>

            {/* Nature of Business */}
            <Form.Item
              label="Nature of Business"
              name="natureOfBusinessId"
              rules={[
                { required: true, message: "Please select nature of business" },
              ]}
            >
              <Select
                showSearch
                placeholder="Select nature of business"
                options={natures}
                filterOption={filterOption}
              />
            </Form.Item>

            {/* Principal Business Activity */}
            <Form.Item
              label="Principal Business Activity"
              name="businessActivity"
              rules={[
                {
                  required: true,
                  message: "Please enter principal business activity",
                },
              ]}
            >
              <Input placeholder="Enter principal business activity" />
            </Form.Item>

            {/* Company's Website */}
            <Form.Item
              label="Company's Website"
              name="companyWebsite"
              rules={[
                { required: true, message: "Please enter company website" },
                { type: "url", message: "Please enter a valid URL" },
              ]}
            >
              <Input placeholder="https://example.com" />
            </Form.Item>

            {/* Email in Incorporation */}
            <Form.Item
              label="Email in Incorporation"
              name="companyEmail"
              rules={[
                { required: true, message: "Please enter email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input type="email" placeholder="Enter email" />
            </Form.Item>

            {/* Mobile Number with Country Code */}
            <Form.Item label="Mobile Number" required>
              <div className="flex w-full gap-2">
                <Form.Item
                  name="countryCode"
                  noStyle
                  rules={[{ required: true, message: "Country code required" }]}
                >
                  <Select
                    showSearch
                    placeholder="Code"
                    filterOption={filterOption}
                    options={countryCodes}
                    style={{ width: "30%" }}
                  />
                </Form.Item>
                <Form.Item
                  name="phoneNumber"
                  noStyle
                  rules={[mobileNumberValidator()]}
                >
                  <Input
                    placeholder="Enter mobile number"
                    maxLength={15}
                    inputMode="numeric"
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </div>
            </Form.Item>

            {/* Does the entity have a place of business in the United States? */}
            <Form.Item
              label="Does the entity have a place of business in the United States?"
              name="isBusinessInUS"
              rules={[{ required: true, message: "Please select an option" }]}
            >
              <Radio.Group>
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </Form.Item>

            {/* Tax Domicile */}
            <Form.Item
              label="Tax Domicile"
              name="taxDomicile"
              rules={[
                { required: true, message: "Please select tax domicile" },
              ]}
            >
              <Select
                showSearch
                placeholder="Select tax domicile"
                filterOption={filterOption}
                options={countries}
              />
            </Form.Item>

            {/* Legal Entity Identifier (LEI) */}
            <Form.Item
              label="Legal Entity Identifier (LEI)"
              name="legalEntityidentify"
            >
              <Input placeholder="Enter LEI (optional)" />
            </Form.Item>

            <Form.Item
              label="CRS Classification"
              name="crsClassifications"
              rules={[
                {
                  required: true,
                  message: "Please select at least one CRS classification",
                },
              ]}
              className="md:col-span-2"
            >
              <Select
                mode="multiple"
                maxTagCount="responsive"
                className="onboarding-multi-select"
                showSearch
                placeholder="Select CRS classification"
                filterOption={filterOption}
                options={CRS_CLASSIFICATION_OPTIONS}
              />
            </Form.Item>
          </div>
        </div>

        {crsClassifications.includes(CRS_PASSIVE_NFE_WITH_CONTROLLING) && (
          <div className="border border-gray-300 rounded-lg p-6 mt-6">
            <h3 className="text-lg font-semibold mb-4">
              CRS Controlling Persons
            </h3>
            <Form.List name="crsControllingPersons">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <div
                      key={field.key}
                      className="border border-gray-200 rounded-lg p-4 mb-4"
                    >
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-medium">
                          Controlling Person {index + 1}
                        </h4>
                        {fields.length > 1 && (
                          <Button
                            type="link"
                            danger
                            onClick={() => remove(field.name)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                          label="Full Name"
                          name={[field.name, "fullName"]}
                          rules={[
                            { required: true, message: "Please enter full name" },
                          ]}
                        >
                          <Input placeholder="Enter full name" />
                        </Form.Item>
                        <Form.Item
                          label="Tax Identification Number"
                          name={[field.name, "taxIdentificationNumber"]}
                        >
                          <Input placeholder="Enter TIN" />
                        </Form.Item>
                        <Form.Item
                          label="Country of Tax Residence"
                          name={[field.name, "countryOfTaxResidenceId"]}
                          rules={[
                            {
                              required: true,
                              message: "Please select country of tax residence",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            placeholder="Select country"
                            filterOption={filterOption}
                            options={countries}
                          />
                        </Form.Item>
                        <Form.Item shouldUpdate noStyle>
                          {({ getFieldValue }) => {
                            const tin = getFieldValue([
                              "crsControllingPersons",
                              index,
                              "taxIdentificationNumber",
                            ]);
                            if (tin) return null;
                            return (
                              <Form.Item
                                label="TIN Not Applicable Reason"
                                name={[field.name, "tinNotApplicableReason"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter reason",
                                  },
                                ]}
                              >
                                <Input placeholder="Enter reason" />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    block
                    onClick={() => add({ ...EMPTY_CONTROLLING_PERSON })}
                  >
                    + Add Controlling Person
                  </Button>
                </>
              )}
            </Form.List>
          </div>
        )}

        {/* Tax Resident Section */}
        <div className="mt-6">
          <p className="text-sm text-gray-600 !font-normal mb-4">
            For Common Reporting Standard (CRS) purposes, please mention which
            countries you are tax resident of? *
          </p>
          <div className="border border-gray-300 rounded-lg p-6 mt-6">
            <div className="mb-6">
              <Form.Item
                label="Are there any other countries you are tax resident of?"
                name="isMultiTaxDomicileEnabled"
                rules={[{ required: true, message: "Please select an option" }]}
              >
                <Radio.Group onChange={handleMultiTaxResidentChange}>
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>
            </div>

            <Form.List name="taxDomiciles">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <div
                      key={field.key}
                      className="border border-gray-300 rounded-lg p-6 mt-6"
                    >
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">
                          {index === 0
                            ? "Tax Resident Information"
                            : `Additional Tax Resident Information ${index}`}
                        </h3>

                        {index !== 0 && (
                          <Button
                            type="link"
                            danger
                            onClick={() => remove(field.name)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                          label="Select Country"
                          name={[field.name, "taxDomicileCountryId"]}
                          rules={[
                            {
                              required: true,
                              message: "Please select country",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            placeholder="Select country"
                            filterOption={filterOption}
                            options={countries}
                          />
                        </Form.Item>

                        <Form.Item shouldUpdate noStyle>
                          {({ getFieldValue }) => {
                            const noTIN = getFieldValue([
                              "taxDomiciles",
                              index,
                              "isTINIDAvailable",
                            ]);

                            return (
                              <Form.Item
                                label="Tax Identification Number"
                                name={[field.name, "taxIDNumber"]}
                                rules={
                                  noTIN
                                    ? []
                                    : [{ required: true, message: "Enter TIN" }]
                                }
                                className="w-full"
                                disabled={noTIN || isFieldDisabled}
                              >
                                <Input
                                  placeholder="Enter tax identification number"
                                  disabled={noTIN || isFieldDisabled}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>

                        <Form.Item
                          name={[field.name, "isTINIDAvailable"]}
                          valuePropName="checked"
                          className="md:col-span-2"
                        >
                          <Checkbox>Check if you don't have TIN?</Checkbox>
                        </Form.Item>

                        <Form.Item shouldUpdate noStyle>
                          {({ getFieldValue }) => {
                            const isTIN = !getFieldValue([
                              "taxDomiciles",
                              index,
                              "isTINIDAvailable",
                            ]);

                            if (isTIN) return null;

                            return (
                              <Form.Item
                                label="Please select a reason"
                                name={[field.name, "taxDomicileReasonID"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Please select reason",
                                  },
                                ]}
                                className="md:col-span-2"
                              >
                                <Radio.Group>
                                  {reasons.map((r) => (
                                    <Radio
                                      key={r.value}
                                      value={r.value}
                                      className="block mb-2"
                                    >
                                      {r.label}
                                    </Radio>
                                  ))}
                                </Radio.Group>
                              </Form.Item>
                            );
                          }}
                        </Form.Item>

                        <Form.Item shouldUpdate noStyle>
                          {({ getFieldValue }) => {
                            const tinUnavailable = getFieldValue([
                              "taxDomiciles",
                              index,
                              "isTINIDAvailable",
                            ]);

                            if (!tinUnavailable) return null;

                            return (
                              <Form.Item
                                label="Business / License Number"
                                name={[field.name, "alternateTaxIdentifier"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter alternate tax identifier",
                                  },
                                ]}
                                className="md:col-span-2"
                              >
                                <Input
                                  placeholder="Business / License Number"
                                  disabled={isFieldDisabled}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </div>
                    </div>
                  ))}

                  {isMultiTaxDomicile && (
                    <div className="mt-6">
                      <Button
                        type="dashed"
                        block
                        onClick={() =>
                          add({
                            taxDomicileCountryId: null,
                            taxIDNumber: "",
                            isTINIDAvailable: false,
                            taxDomicileReasonID: null,
                            alternateTaxIdentifier: "",
                          })
                        }
                      >
                        + Add Another Tax Resident Country
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Form.List>
          </div>
        </div>

        {/* Registered Address Section */}
        <div className="border border-gray-300 rounded-lg p-6 mt-6">
          <h3 className="text-lg font-semibold mb-4">Registered address</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Apartment/Villa Number/Name */}
            <Form.Item
              label="Apartment/Villa Number/Name"
              name="companyApartmentVillaInfo"
              rules={[
                {
                  required: true,
                  message: "Please enter apartment/villa number/name",
                },
              ]}
            >
              <Input
                placeholder="Enter apartment/villa number/name"
                onChange={handleRegisteredAddressChange}
              />
            </Form.Item>

            {/* Street Name/Area */}
            <Form.Item
              label="Street Name/Area"
              name="companyStreetAreaInfo"
              rules={[
                { required: true, message: "Please enter street name/area" },
              ]}
            >
              <Input
                placeholder="Enter street name/area"
                onChange={handleRegisteredAddressChange}
              />
            </Form.Item>

            {/* Country */}
            <Form.Item
              label="Country"
              name="companyCountryCode"
              rules={[{ required: true, message: "Please select country" }]}
            >
              <Select
                showSearch
                placeholder="Select country"
                filterOption={filterOption}
                options={countries}
                onChange={handleRegisteredAddressChange}
              />
            </Form.Item>

            {/* City / Town */}
            <Form.Item
              label="City / Town"
              name="companyCity"
              rules={[{ required: true, message: "Please enter city/town" }]}
            >
              <Input
                placeholder="Enter city/town"
                onChange={handleRegisteredAddressChange}
              />
            </Form.Item>

            {/* Postal / Zip Code */}
            <Form.Item
              label="Postal / Zip Code"
              name="companyZipCode"
              rules={[
                { required: true, message: "Please enter postal/zip code" },
              ]}
            >
              <Input
                placeholder="Enter postal/zip code"
                onChange={handleRegisteredAddressChange}
              />
            </Form.Item>
          </div>
        </div>

        {/* Business Address Section */}
        <div className="border border-gray-300 rounded-lg p-6 mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Business address</h3>
            <Form.Item
              name="IsSameAsAbove"
              valuePropName="checked"
              className="mb-0"
            >
              <Checkbox onChange={handleSameAsAboveChange}>
                Same as Above
              </Checkbox>
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Apartment/Villa Number/Name */}
            <Form.Item
              label="Apartment/Villa Number/Name"
              name="businessApartmentVillaInfo"
              rules={[
                {
                  required: true,
                  message: "Please enter apartment/villa number/name",
                },
              ]}
            >
              <Input
                placeholder="Enter apartment/villa number/name"
                disabled={isFieldDisabled || (!isReviewMode && sameAsAbove)}
              />
            </Form.Item>

            {/* Street Name/Area */}
            <Form.Item
              label="Street Name/Area"
              name="businessStreetAreaInfo"
              rules={[
                { required: true, message: "Please enter street name/area" },
              ]}
            >
              <Input
                placeholder="Enter street name/area"
                disabled={isFieldDisabled || (!isReviewMode && sameAsAbove)}
              />
            </Form.Item>

            {/* Country */}
            <Form.Item
              label="Country"
              name="businessCountryCode"
              rules={[{ required: true, message: "Please select country" }]}
            >
              <Select
                showSearch
                placeholder="Select country"
                filterOption={filterOption}
                options={countries}
                disabled={isFieldDisabled || (!isReviewMode && sameAsAbove)}
              />
            </Form.Item>

            {/* City / Town */}
            <Form.Item
              label="City / Town"
              name="businessCity"
              rules={[{ required: true, message: "Please enter city/town" }]}
            >
              <Input
                placeholder="Enter city/town"
                disabled={isFieldDisabled || (!isReviewMode && sameAsAbove)}
              />
            </Form.Item>

            {/* Postal / Zip Code */}
            <Form.Item
              label="Postal / Zip Code"
              name="businessZipCode"
              rules={[
                { required: true, message: "Please enter postal/zip code" },
              ]}
            >
              <Input
                placeholder="Enter postal/zip code"
                disabled={isFieldDisabled || (!isReviewMode && sameAsAbove)}
              />
            </Form.Item>
          </div>
        </div>

        {isDisabled ? null : (
          <div className="flex justify-between mt-8">
            <Button onClick={onPrevious} disabled={true}>
              Previous
            </Button>
            <Button type="primary" htmlType="submit" loading={isSubmitLoading}>
              {isSubmitLoading ? "Submitting..." : "Submit"}
            </Button>
          </div>
        )}
      </Form>
      {isDisabled && (
        <div className="flex justify-between mt-8">
          <Button onClick={onPrevious} disabled={true}>
            Previous
          </Button>
          <Button type="primary" onClick={onNext}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default CompanyInfo;
